import { RolesEnum } from '../types/RolesEnum';

/**
 * Orden e i18n de la “familia flujo” en Selección de rol / switcher.
 *
 * El Administrador ya no sale en /specialist-role-selection: parametriza el motor
 * bajo Administración. El Coordinador va al final y opera el flujo (override).
 *
 * Cuándo tocar: nuevo rol que deba verse/ordenarse junto a Feeder/Pre/G/V/E/Coordinator.
 * No uses esta lista para decidir tarjetas de módulos en /admin-user-role.
 */
export const SPECIALIST_ROLE_DISPLAY_ORDER = [
  RolesEnum.SPECIALIST_FEEDER,
  RolesEnum.SPECIALIST_PRE_MANAGER,
  RolesEnum.SPECIALIST_MANAGER,
  RolesEnum.SPECIALIST_VALIDATOR,
  RolesEnum.SPECIALIST_EDITOR,
  RolesEnum.SPECIALIST_LIAISON,
  RolesEnum.SPECIALIST_COORDINATOR
];

/**
 * Roles que SÍ parametrizan módulos en /admin-user-role (userrolemodules).
 *
 * Sin Administrador: el Coordinador opera CMI/bandejas; si no tiene
 * userrolemodules, el CMI cae a los 8 módulos. Un admin-only no necesita
 * Especialista ni G/V/E ni checkboxes de módulo.
 *
 * Cuándo tocar: un rol especialista nuevo que deba tener tarjeta
 * “Módulos por rol” (añádelo también a DISPLAY_ORDER si aplica).
 * Si el rol es solo intención de sesión (como Admin/Supervisor), no lo pongas aquí.
 */
export const SPECIALIST_MODULE_ROLE_NAMES = [
  RolesEnum.SPECIALIST_FEEDER,
  RolesEnum.SPECIALIST_PRE_MANAGER,
  RolesEnum.SPECIALIST_MANAGER,
  RolesEnum.SPECIALIST_VALIDATOR,
  RolesEnum.SPECIALIST_VERIFICATOR,
  RolesEnum.SPECIALIST_EDITOR,
  RolesEnum.SPECIALIST_COORDINATOR,
  RolesEnum.SPECIALIST_LIAISON
];

export const FLOW_ENGINE_ONLY_ROLE_NAMES = new Set([
  RolesEnum.SPECIALIST_FEEDER,
  RolesEnum.SPECIALIST_PRE_MANAGER,
  RolesEnum.SPECIALIST_MANAGER,
  RolesEnum.SPECIALIST_VALIDATOR,
  RolesEnum.SPECIALIST_VERIFICATOR,
  RolesEnum.SPECIALIST_EDITOR,
  RolesEnum.SPECIALIST_COORDINATOR,
  RolesEnum.SPECIALIST_LIAISON
]);

/** El catálogo V3 usa Specialist-Verificator; el código interno sigue en validator. */
export const canonicalizeSpecialistRoleName = (roleName = '') => {
  if (roleName === RolesEnum.SPECIALIST_VERIFICATOR) {
    return RolesEnum.SPECIALIST_VALIDATOR;
  }
  if (roleName === 'Specialist-Leader') {
    return RolesEnum.SPECIALIST_FEEDER;
  }
  return roleName;
};

/** True solo si el rol pide tarjeta de módulos. Lee SPECIALIST_MODULE_ROLE_NAMES, no DISPLAY_ORDER. */
export const isSpecialistModuleRoleName = (roleName) => {
  const canonical = canonicalizeSpecialistRoleName(roleName);
  return SPECIALIST_MODULE_ROLE_NAMES.includes(roleName)
    || SPECIALIST_MODULE_ROLE_NAMES.includes(canonical);
};

export const getRoleI18nKey = (roleName) => {
  const map = {
    [RolesEnum.ADMINISTRATOR]: 'roles.administrator',
    [RolesEnum.SPECIALIST_FEEDER]: 'roles.specialist-feeder',
    [RolesEnum.SPECIALIST_PRE_MANAGER]: 'roles.specialist-pre-manager',
    [RolesEnum.SPECIALIST_MANAGER]: 'roles.specialist-manager',
    [RolesEnum.SPECIALIST_VALIDATOR]: 'roles.specialist-validator',
    [RolesEnum.SPECIALIST_VERIFICATOR]: 'roles.specialist-validator',
    [RolesEnum.SPECIALIST_EDITOR]: 'roles.specialist-editor',
    [RolesEnum.SPECIALIST_COORDINATOR]: 'roles.specialist-coordinator',
    [RolesEnum.SPECIALIST_LIAISON]: 'roles.specialist-liaison',
    [RolesEnum.END_USER]: 'roles.end-user',
    [RolesEnum.END_USER_INSTITUTIONAL]: 'roles.end-user-institutional',
    [RolesEnum.CLIENT]: 'roles.client',
    [RolesEnum.REPORTER]: 'roles.reporter'
  };
  return map[roleName] || null;
};

export const getRoleDisplayLabel = (roleName, t) => {
  const i18nKey = getRoleI18nKey(roleName);
  return i18nKey ? t(i18nKey) : roleName;
};

export const sortRolesByDisplayOrder = (roles = []) => {
  const orderIndex = (roleName) => {
    const flowIndex = SPECIALIST_ROLE_DISPLAY_ORDER.indexOf(canonicalizeSpecialistRoleName(roleName));
    if (flowIndex !== -1) {
      return flowIndex;
    }
    return 1000;
  };

  return [...roles].sort((left, right) => {
    const leftOrder = orderIndex(left.name);
    const rightOrder = orderIndex(right.name);

    if (leftOrder !== rightOrder) {
      return leftOrder - rightOrder;
    }

    return String(left.name).localeCompare(String(right.name));
  });
};

export const buildModuleCheckState = (catalogModules = [], listUserRoleModules = [], catalogroleId) => {
  const roleId = Number(catalogroleId);

  return catalogModules.map((moduleItem) => {
    const isAssigned = listUserRoleModules.some((item) => {
      const moduleId = item?.CatalogModule_To_UserRoleModule?.id;
      const itemRoleId = Number(item?.catalogrole_id ?? 2);
      return moduleId === moduleItem.id && itemRoleId === roleId;
    });

    return {
      ...moduleItem,
      value: isAssigned
    };
  });
};

export const collectModulesToSave = (modulesByRoleId = {}, userId) => {
  const payload = [];

  Object.entries(modulesByRoleId).forEach(([catalogroleId, modules]) => {
    (modules || []).forEach((moduleItem) => {
      if (moduleItem.value) {
        payload.push({
          user_id: userId,
          catalogmodule_id: moduleItem.id,
          catalogrole_id: Number(catalogroleId),
          deleted: false
        });
      }
    });
  });

  return payload;
};
